import { FC, MouseEvent, useRef } from "react";
import styles from "./ReceiptRow.module.scss";
import { useTextFromTranslations } from "@/helpers/translation";
import { Amount, RowClickCallback } from "@/components/amount";
import { formatMoney } from "@/helpers/numbers";
import classNames from "classnames";
import Translations from "@/types/translations";
import Label from "@/components/label";
import { useTranslation } from "react-i18next";

interface ReceiptRowProps {
  translations?: Translations[];
  pricePerUnit?: number;
  quantity: number;
  combiDealAvailable?: boolean | null;
  combiDealApplied?: boolean;
  minimumQuantity?: number;
  maximumQuantity?: number;
  onIncrement?: () => void;
  onDecrement?: () => void;
  onRemoveAll?: () => void;
  onReshowDealModal?: () => void;
  isDiscount?: boolean;
}

const ReceiptRow: FC<ReceiptRowProps> = ({
  translations,
  pricePerUnit = 0,
  quantity,
  combiDealAvailable,
  combiDealApplied,
  minimumQuantity = 1,
  maximumQuantity,
  onIncrement,
  onDecrement,
  onRemoveAll,
  onReshowDealModal,
  isDiscount = false,
}) => {
  const { t } = useTranslation();
  const translation = useTextFromTranslations(translations);
  const name = translation?.name;
  const description = translation?.description;

  const onRowClickRef = useRef<RowClickCallback>();
  const containerRef = useRef<HTMLDivElement>(null);

  const onRowClick = (event?: MouseEvent<HTMLDivElement>) => {
    onRowClickRef.current?.(event);
  };

  return (
    <div
      className={styles.container}
      ref={containerRef}
      onClick={onRowClick}
      data-cy={isDiscount ? "discount-row" : "product-row"}
    >
      <div className="row">
        <Amount
          amount={quantity}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
          onRemoveAll={onRemoveAll}
          minimum={minimumQuantity}
          maximum={maximumQuantity}
          customClickCallbackRef={onRowClickRef}
          outsideClickElementRef={containerRef}
        />
        <div className={styles.textWrapper}>
          <p className={styles.productTitle} data-cy="receipt-row-name">
            {name}
          </p>
          {description && <p className={styles.productDescription}>{description}</p>}
        </div>
      </div>
      <div className="row">
        {combiDealAvailable && (
          <Label
            title={
              combiDealApplied ? t("mainPage.combiDeals.deal") : t("mainPage.combiDeals.title")
            }
            info={!combiDealApplied}
            className={styles.dealLabel}
            data-cy={combiDealApplied ? "applied-deal-label" : "unapplied-deal-label"}
            onClick={combiDealApplied ? undefined : onReshowDealModal}
          />
        )}
        <div className={classNames("h5", styles.price)} data-cy="total-price">
          {formatMoney((pricePerUnit * quantity) / 100)}
        </div>
      </div>
    </div>
  );
};

export { ReceiptRow };
